import React, { useState, useEffect } from 'react';
import { X, UserPlus } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';
import CompanyAutocompleteInput from '../shared/CompanyAutocompleteInput';
import CreateClientModal from './CreateClientModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateOpportunityModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    client_label: '',        // display label for autocomplete UX
    selected_source: null,   // {source, id, company_name, contact_person, email, phone}
    value: '',
    currency: 'USD',
    stage: 'prospecting',
    probability: 50,
    expected_close_date: '',
    description: '',
    assigned_to: '',
    tags: ''
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showQuickCreateClient, setShowQuickCreateClient] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crm/clients?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch clients');

      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        title: 'Error',
        description: 'Failed to load clients',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Opportunity name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.client_id && !formData.selected_source) {
      toast({
        title: 'Validation Error',
        description: 'Please select a client',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.value || parseFloat(formData.value) <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Value must be greater than 0',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);

      // F2.x — Resolve-or-Create client: if the user selected a
      // non-client entity (counterparty/supplier/lead/onboarding) via
      // the autocomplete, auto-create a CRM Client from it so the
      // opportunity keeps a valid client_id FK.
      let resolvedClientId = formData.client_id;
      if (!resolvedClientId && formData.selected_source) {
        const s = formData.selected_source;
        const createPayload = {
          company_name:    s.company_name || formData.client_label,
          contact_person:  s.contact_person || s.contact_name || 'N/A',
          email:           s.email || `noreply+${(s.id || 'x').slice(0,8)}@grupobmp.com`,
          phone:           s.phone || null,
          tax_id:          s.trn || s.tax_id || null,
          industry:        s.industry || null,
          tags:            [s.source === 'lead' ? 'auto-from-lead' : `auto-from-${s.source}`],
          notes:           `Auto-created from ${s.source} during Opportunity creation.`,
        };
        try {
          const cr = await fetch(`${API_URL}/api/crm/clients`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(createPayload),
          });
          if (cr.ok) {
            const newClient = await cr.json();
            resolvedClientId = newClient.id;
            toast({
              title: 'Client auto-created',
              description: `${createPayload.company_name} is now a CRM Client.`,
            });
          } else {
            // Fall back: maybe a duplicate — try to find by email/name in fresh list
            const listR = await fetch(`${API_URL}/api/crm/clients?limit=1000`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (listR.ok) {
              const all = await listR.json();
              const existing = all.find(
                (c) => c.email === createPayload.email
                    || (c.company_name || '').toLowerCase() === (createPayload.company_name || '').toLowerCase()
              );
              if (existing) resolvedClientId = existing.id;
            }
          }
        } catch (_) { /* fall through to error toast below */ }
      }

      if (!resolvedClientId) {
        toast({
          title: 'Could not resolve client',
          description: 'Please pick an existing client from the fallback dropdown or create one first.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Prepare tags array
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      const payload = {
        name: formData.name.trim(),
        client_id: resolvedClientId,
        value: parseFloat(formData.value),
        currency: formData.currency,
        stage: formData.stage,
        probability: parseInt(formData.probability),
        expected_close_date: formData.expected_close_date || null,
        description: formData.description.trim() || null,
        assigned_to: formData.assigned_to.trim() || null,
        tags: tagsArray
      };

      const response = await fetch(`${API_URL}/api/crm/opportunities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create opportunity');
      }

      toast({
        title: 'Success',
        description: 'Opportunity created successfully'
      });

      // Reset form
      setFormData({
        name: '',
        client_id: '',
        client_label: '',
        selected_source: null,
        value: '',
        currency: 'USD',
        stage: 'prospecting',
        probability: 50,
        expected_close_date: '',
        description: '',
        assigned_to: '',
        tags: ''
      });

      onSuccess();
    } catch (error) {
      console.error('Error creating opportunity:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create opportunity',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#2B2D42]">Create New Opportunity</h2>
          <button
            onClick={onClose}
            className="text-[#6C757D] hover:text-[#2B2D42]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Opportunity Name */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Opportunity Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="e.g., Q1 2026 Trading Deal"
              required
            />
          </div>

          {/* Client — unified autocomplete (searches clients + counterparties + suppliers + leads + onboarding) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#2B2D42]">
                Client *
              </label>
              <button
                type="button"
                onClick={() => setShowQuickCreateClient(true)}
                className="text-xs font-semibold text-[#00B4D8] hover:text-[#0096B8] inline-flex items-center gap-1"
                data-testid="opportunity-quick-create-client-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> + New Client
              </button>
            </div>
            <CompanyAutocompleteInput
              testid="opportunity-input-client"
              value={formData.client_label}
              onChange={(v) => setFormData({ ...formData, client_label: v,
                // clear resolved selection on typing
                client_id: '', selected_source: null })}
              onSelect={(s) => {
                // If the selected entry is already a CRM client, use its id directly.
                let clientId = '';
                if (s.source === 'client' && s.id) clientId = s.id;
                // If it's a CRM client matching our fetched list, pick that id.
                if (!clientId) {
                  const match = clients.find(
                    (c) => (c.company_name || '').toLowerCase() === (s.company_name || '').toLowerCase()
                  );
                  if (match) clientId = match.id;
                }
                setFormData((p) => ({
                  ...p,
                  client_id: clientId,
                  client_label: s.company_name || s.label || p.client_label,
                  selected_source: s,
                }));
              }}
              placeholder="Type to search clients, counterparties, suppliers, leads…"
            />
            {formData.selected_source && !formData.client_id && (
              <div className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                data-testid="opportunity-client-autocreate-hint">
                <b>{formData.selected_source.company_name}</b> is not yet a CRM Client —
                it will be auto-created from the existing {formData.selected_source.source} record on submit.
              </div>
            )}
            {/* Legacy dropdown kept as fallback for direct-pick from existing clients */}
            {clients.length > 0 && (
              <details className="mt-2">
                <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-[#0F172A]">
                  Or pick from existing CRM Clients ({clients.length})
                </summary>
                <select
                  value={formData.client_id}
                  data-testid="opportunity-client-dropdown"
                  onChange={(e) => {
                    const c = clients.find((x) => x.id === e.target.value);
                    setFormData((p) => ({
                      ...p,
                      client_id: e.target.value,
                      client_label: c ? c.company_name : p.client_label,
                      selected_source: c ? { source: 'client', ...c } : null,
                    }));
                  }}
                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]">
                  <option value="">Select…</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company_name}{client.contact_person ? ` — ${client.contact_person}` : ''}
                    </option>
                  ))}
                </select>
              </details>
            )}
          </div>

          {/* Value and Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Value *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="10000.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              >
                <option value="USD">USD</option>
                <option value="BRL">BRL</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          {/* Stage and Probability */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Stage
              </label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              >
                <option value="prospecting">Prospecting</option>
                <option value="qualification">Qualification</option>
                <option value="proposal">Proposal</option>
                <option value="negotiation">Negotiation</option>
                <option value="closed_won">Closed Won</option>
                <option value="closed_lost">Closed Lost</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Probability (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              />
            </div>
          </div>

          {/* Expected Close Date */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Expected Close Date
            </label>
            <input
              type="date"
              value={formData.expected_close_date}
              onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            />
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Assigned To
            </label>
            <input
              type="text"
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="Sales Manager Name"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Tags
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="e.g., high-priority, trading, Q1 (comma separated)"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="Additional details about this opportunity..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Opportunity'}
            </Button>
          </div>
        </form>
      </div>

      {/* Inline quick-create client (modal-on-modal) */}
      {showQuickCreateClient && (
        <CreateClientModal
          isOpen={showQuickCreateClient}
          onClose={() => setShowQuickCreateClient(false)}
          onSuccess={(newClient) => {
            // Refresh client list and pre-select the just-created client
            setShowQuickCreateClient(false);
            fetchClients();
            if (newClient && newClient.id) {
              setFormData((p) => ({
                ...p,
                client_id: newClient.id,
                client_label: newClient.company_name || newClient.contact_person || 'New Client',
                selected_source: { source: 'client', ...newClient },
              }));
              toast({
                title: 'Client linked',
                description: `${newClient.company_name || 'Client'} is now selected for this opportunity.`,
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default CreateOpportunityModal;
